/**
 * Native PowerPoint export (pptxgenjs). Layout rails follow pptx-html-fidelity-audit
 * footer-rail + cursor-flow rules. Thai fonts never use italic.
 *
 * Fonts: Leelawadee UI (primary) → Tahoma → Sarabun. Glyphs come from the viewer's OS.
 */

import type { ExportCellValue, ExportWorkbookSheet } from './exportService';
import type { PptxNativeChartSpec } from './pptxNativeChart';
import { chunkPptxNativeChart, isValidPptxNativeChart } from './pptxNativeChart';

export type PptxMetaRow = [string, string];

export type PptxSection = {
  title: string;
  headers: string[];
  rows: string[][];
  metaRows?: PptxMetaRow[];
};

export type PptxKpi = {
  id: string;
  label: string;
  value: string;
};

/** Soft projected-slide disclosure; download still proceeds. */
export const PPTX_SOFT_SLIDE_BUDGET = 80;

const CANVAS_W = 13.333;
const CANVAS_H = 7.5;
const MARGIN_X = 0.6;
const MARGIN_TOP = 0.5;
const CONTENT_MAX_Y = 6.7;
const FOOTER_TOP = 6.85;
const CONTENT_W = CANVAS_W - MARGIN_X * 2;

/** Max data columns per slide (identity columns counted separately when repeating). */
const MAX_DATA_COLS = 8;
const DEFAULT_ROWS_PER_SLIDE = 14;
const IDENTITY_HEADER_HINTS = ['หน่วยงาน', 'เดือน', 'ช่วงเวลา', 'ประทับเวลา', 'วันที่เกิดเหตุการณ์'];

const COLOR_INK = '0F172A';
const COLOR_MUTED = '64748B';
const COLOR_PAPER = 'FFFFFF';
const COLOR_HEADER_BG = '475569';
const COLOR_ACCENT = '4F46E5';
const COLOR_CARD_BG = 'F1F5F9';

/** Leelawadee UI → Tahoma → Sarabun (see module comment). Never italicize Thai. */
const FONT_THAI = 'Leelawadee UI';

const PLACEHOLDER_NO_DATA = 'ไม่มีข้อมูลในช่วงที่เลือก';

type PptxGenJsModule = typeof import('pptxgenjs');
type PptxGenJS = InstanceType<PptxGenJsModule['default']>;
type PptxSlide = ReturnType<PptxGenJS['addSlide']>;

class Cursor {
  y: number;
  constructor(
    yStart = MARGIN_TOP,
    private readonly cap = CONTENT_MAX_Y,
  ) {
    this.y = yStart;
  }
  take(h: number, gap = 0.12): number {
    const top = this.y;
    this.y = top + h + gap;
    if (this.y > this.cap + 0.001) {
      throw new OverflowError(`cursor ${this.y} exceeds rail ${this.cap}`);
    }
    return top;
  }
  remaining(): number {
    return this.cap - this.y;
  }
}

class OverflowError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OverflowError';
  }
}

export function sanitizeDownloadFilename(raw: string, ext: 'pptx'): string {
  const stripped = raw.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim() || 'export';
  const withoutExt = stripped.replace(new RegExp(`\\.${ext}$`, 'i'), '');
  return `${withoutExt}.${ext}`;
}

export function cellToPptxString(value: ExportCellValue): string {
  if (value == null) return '—';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '—' : value.toLocaleDateString('th-TH');
  }
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  const text = String(value).trim();
  return text.length ? text : '—';
}

function isEmptyRow(row: ExportCellValue[] | undefined): boolean {
  if (!row || row.length === 0) return true;
  return row.every((cell) => cell == null || String(cell).trim() === '');
}

/**
 * Convert workbook AOA sheets to PPTX sections.
 * คำอธิบาย → cover meta only. HR sheets: leading key/value pairs then header. Nurse notes: first row headers.
 */
export function workbookSheetsToPptxSections(sheets: ExportWorkbookSheet[]): {
  sections: PptxSection[];
  legendMeta: PptxMetaRow[];
} {
  const legendMeta: PptxMetaRow[] = [];
  const sections: PptxSection[] = [];

  for (const sheet of sheets) {
    if (sheet.sheetName === 'คำอธิบาย') {
      for (const row of sheet.rows) {
        if (isEmptyRow(row) || row.length < 2) continue;
        legendMeta.push([cellToPptxString(row[0]), cellToPptxString(row[1] ?? null)]);
      }
      continue;
    }

    const rows = sheet.rows;
    if (!rows.length) continue;

    const firstNonEmptyIdx = rows.findIndex((row) => !isEmptyRow(row));
    if (firstNonEmptyIdx < 0) continue;
    const firstNonEmpty = rows[firstNonEmptyIdx];

    // HR pattern: leading key/value pairs, blank row, then a wider header row.
    let scan = firstNonEmptyIdx;
    let onlyPairs = true;
    while (scan < rows.length && !isEmptyRow(rows[scan])) {
      if (rows[scan].length !== 2) {
        onlyPairs = false;
        break;
      }
      scan += 1;
    }
    const hasBlank = scan < rows.length && isEmptyRow(rows[scan]);
    let afterBlank = scan + 1;
    while (afterBlank < rows.length && isEmptyRow(rows[afterBlank])) afterBlank += 1;
    const headerAfterMeta =
      onlyPairs &&
      hasBlank &&
      afterBlank < rows.length &&
      (rows[afterBlank]?.length ?? 0) >= 2;

    if (headerAfterMeta) {
      const sectionMeta: PptxMetaRow[] = [];
      for (let i = firstNonEmptyIdx; i < scan; i += 1) {
        const row = rows[i];
        sectionMeta.push([cellToPptxString(row[0]), cellToPptxString(row[1] ?? null)]);
      }
      const headers = rows[afterBlank].map(cellToPptxString);
      const dataRows = rows
        .slice(afterBlank + 1)
        .filter((row) => !isEmptyRow(row))
        .map((row) => headers.map((_, col) => cellToPptxString(row[col] ?? null)));
      sections.push({
        title: sheet.sheetName,
        headers,
        rows: dataRows.length ? dataRows : [[PLACEHOLDER_NO_DATA]],
        metaRows: sectionMeta.length ? sectionMeta : undefined,
      });
      continue;
    }

    // Default (nurse_notes and simple tables): first non-empty row is the header.
    const headers = firstNonEmpty.map(cellToPptxString);
    const dataRows = rows
      .slice(firstNonEmptyIdx + 1)
      .filter((row) => !isEmptyRow(row))
      .map((row) => headers.map((_, col) => cellToPptxString(row[col] ?? null)));
    sections.push({
      title: sheet.sheetName,
      headers,
      rows: dataRows.length ? dataRows : [[PLACEHOLDER_NO_DATA]],
    });
  }

  return { sections, legendMeta };
}

function identityColumnIndexes(headers: string[]): number[] {
  const indexes: number[] = [];
  headers.forEach((header, index) => {
    if (IDENTITY_HEADER_HINTS.some((hint) => header.includes(hint))) {
      indexes.push(index);
    }
  });
  if (indexes.length === 0 && headers.length > 0) indexes.push(0);
  return indexes.slice(0, 3);
}

export function chunkHeadersByWidth(
  headers: string[],
  maxDataCols = MAX_DATA_COLS,
): Array<{ headers: string[]; sourceIndexes: number[]; colRangeLabel: string }> {
  if (headers.length === 0) {
    return [{ headers: [PLACEHOLDER_NO_DATA], sourceIndexes: [0], colRangeLabel: 'คอลัมน์ 1–1' }];
  }
  if (headers.length <= maxDataCols + 2) {
    return [
      {
        headers,
        sourceIndexes: headers.map((_, i) => i),
        colRangeLabel: `คอลัมน์ 1–${headers.length}`,
      },
    ];
  }

  const identity = identityColumnIndexes(headers);
  const identitySet = new Set(identity);
  const dataIndexes = headers.map((_, i) => i).filter((i) => !identitySet.has(i));
  const chunks: Array<{ headers: string[]; sourceIndexes: number[]; colRangeLabel: string }> = [];

  for (let start = 0; start < dataIndexes.length; start += maxDataCols) {
    const slice = dataIndexes.slice(start, start + maxDataCols);
    const sourceIndexes = [...identity, ...slice];
    const unique = Array.from(new Set(sourceIndexes));
    const startCol = Math.min(...slice) + 1;
    const endCol = Math.max(...slice) + 1;
    chunks.push({
      headers: unique.map((i) => headers[i]),
      sourceIndexes: unique,
      colRangeLabel: `คอลัมน์ ${startCol}–${endCol}`,
    });
  }
  return chunks;
}

function projectRows(rows: string[][], sourceIndexes: number[]): string[][] {
  return rows.map((row) => sourceIndexes.map((i) => row[i] ?? '—'));
}

function rowsPerSlideForColumnCount(colCount: number): number {
  if (colCount >= 10) return 10;
  if (colCount >= 7) return 12;
  return DEFAULT_ROWS_PER_SLIDE;
}

export function estimatePptxSlideCount(args: {
  sections?: PptxSection[];
  headers?: string[];
  rows?: string[][];
  includeCover?: boolean;
  includeKpis?: boolean;
}): number {
  let count = args.includeCover ? 1 : 0;
  if (args.includeKpis) count += 1;

  const sections =
    args.sections?.length
      ? args.sections
      : args.headers
        ? [{ title: '', headers: args.headers, rows: args.rows ?? [] }]
        : [];

  for (const section of sections) {
    const chunks = chunkHeadersByWidth(section.headers);
    for (const chunk of chunks) {
      const projected = projectRows(section.rows, chunk.sourceIndexes);
      const per = rowsPerSlideForColumnCount(chunk.headers.length);
      count += Math.max(1, Math.ceil(Math.max(projected.length, 1) / per));
    }
  }
  return Math.max(count, 1);
}

async function loadPptxGenJs(): Promise<PptxGenJsModule['default']> {
  const mod = await import('pptxgenjs');
  return mod.default;
}

function createDeck(PptxGenJS: PptxGenJsModule['default']): PptxGenJS {
  const pres = new PptxGenJS();
  pres.defineLayout({ name: 'LAYOUT_WIDE', width: CANVAS_W, height: CANVAS_H });
  pres.layout = 'LAYOUT_WIDE';
  return pres;
}

function addChromeFooter(slide: PptxSlide, left: string, page: number, total: number): void {
  slide.addText(left || 'NiData', {
    x: MARGIN_X,
    y: FOOTER_TOP,
    w: CONTENT_W * 0.7,
    h: 0.28,
    fontSize: 10,
    fontFace: FONT_THAI,
    color: COLOR_MUTED,
    margin: 0,
  });
  slide.addText(`${page} / ${total}`, {
    x: MARGIN_X + CONTENT_W * 0.7,
    y: FOOTER_TOP,
    w: CONTENT_W * 0.3,
    h: 0.28,
    fontSize: 10,
    fontFace: FONT_THAI,
    color: COLOR_MUTED,
    align: 'right',
    margin: 0,
  });
}

function addMetaBlock(slide: PptxSlide, cursor: Cursor, metaRows: PptxMetaRow[]): void {
  if (!metaRows.length) return;
  const lines = metaRows.map(([k, v]) => `${k}: ${v}`).join('\n');
  const h = Math.min(0.22 * metaRows.length + 0.08, cursor.remaining() - 0.2);
  if (h <= 0.15) return;
  const top = cursor.take(h, 0.16);
  slide.addText(lines, {
    x: MARGIN_X,
    y: top,
    w: CONTENT_W,
    h,
    fontSize: 11,
    fontFace: FONT_THAI,
    color: COLOR_INK,
    margin: 0,
    valign: 'top',
  });
}

function addTitle(slide: PptxSlide, cursor: Cursor, title: string, subtitle?: string): void {
  const titleH = 0.55;
  const top = cursor.take(titleH, subtitle ? 0.06 : 0.14);
  slide.addText(title, {
    x: MARGIN_X,
    y: top,
    w: CONTENT_W,
    h: titleH,
    fontSize: 28,
    fontFace: FONT_THAI,
    bold: true,
    color: COLOR_INK,
    margin: 0,
  });
  if (subtitle) {
    const subH = 0.35;
    const subTop = cursor.take(subH, 0.14);
    slide.addText(subtitle, {
      x: MARGIN_X,
      y: subTop,
      w: CONTENT_W,
      h: subH,
      fontSize: 14,
      fontFace: FONT_THAI,
      color: COLOR_MUTED,
      margin: 0,
    });
  }
}

function addKpiCards(slide: PptxSlide, cursor: Cursor, kpis: PptxKpi[]): void {
  if (!kpis.length) return;
  const cardH = 1.35;
  const gap = 0.2;
  const count = Math.min(kpis.length, 4);
  const cardW = (CONTENT_W - gap * (count - 1)) / count;
  const top = cursor.take(cardH, 0.2);
  kpis.slice(0, count).forEach((kpi, index) => {
    const x = MARGIN_X + index * (cardW + gap);
    slide.addShape('roundRect', {
      x,
      y: top,
      w: cardW,
      h: cardH,
      fill: { color: COLOR_CARD_BG },
      rectRadius: 0.08,
    });
    slide.addText(kpi.label, {
      x: x + 0.12,
      y: top + 0.14,
      w: cardW - 0.24,
      h: 0.55,
      fontSize: 11,
      fontFace: FONT_THAI,
      color: COLOR_MUTED,
      margin: 0,
      valign: 'top',
    });
    slide.addText(kpi.value, {
      x: x + 0.12,
      y: top + 0.7,
      w: cardW - 0.24,
      h: 0.45,
      fontSize: 22,
      fontFace: FONT_THAI,
      bold: true,
      color: COLOR_ACCENT,
      margin: 0,
    });
  });
}

function addTableChunk(
  slide: PptxSlide,
  cursor: Cursor,
  headers: string[],
  rows: string[][],
  sectionTitle?: string,
): void {
  if (sectionTitle) {
    const h = 0.32;
    const top = cursor.take(h, 0.08);
    slide.addText(sectionTitle, {
      x: MARGIN_X,
      y: top,
      w: CONTENT_W,
      h,
      fontSize: 14,
      fontFace: FONT_THAI,
      bold: true,
      color: COLOR_INK,
      margin: 0,
    });
  }

  const remaining = cursor.remaining();
  const tableH = Math.max(0.8, remaining - 0.05);
  const top = cursor.take(tableH, 0);

  const tableRows = [
    headers.map((text) => ({
      text,
      options: {
        bold: true,
        color: 'FFFFFF',
        fill: { color: COLOR_HEADER_BG },
        align: 'left' as const,
        valign: 'middle' as const,
        fontFace: FONT_THAI,
        fontSize: 9,
      },
    })),
    ...rows.map((row) =>
      row.map((text) => ({
        text,
        options: {
          color: COLOR_INK,
          align: 'left' as const,
          valign: 'top' as const,
          fontFace: FONT_THAI,
          fontSize: 9,
        },
      })),
    ),
  ];

  slide.addTable(tableRows as never, {
    x: MARGIN_X,
    y: top,
    w: CONTENT_W,
    h: tableH,
    border: [
      { pt: 0.5, color: 'CBD5E1' },
      { pt: 0.5, color: 'CBD5E1' },
      { pt: 0.5, color: 'CBD5E1' },
      { pt: 0.5, color: 'CBD5E1' },
    ],
    colW: headers.map(() => CONTENT_W / Math.max(headers.length, 1)),
    fontFace: FONT_THAI,
    color: COLOR_INK,
    align: 'left',
    valign: 'top',
  });
}

type BuiltSlide = {
  paint: (slide: PptxSlide, page: number, total: number, pres: PptxGenJS) => void;
};

function normalizeSections(args: {
  sections?: PptxSection[];
  headers?: string[];
  rows?: string[][];
}): PptxSection[] {
  if (args.sections?.length) return args.sections;
  if (args.headers?.length) {
    const rows = args.rows?.length ? args.rows : [[PLACEHOLDER_NO_DATA]];
    return [{ title: '', headers: args.headers, rows }];
  }
  return [{ title: '', headers: ['รายการ'], rows: [[PLACEHOLDER_NO_DATA]] }];
}

function buildTableSlides(
  sections: PptxSection[],
  footerLeft: string,
): BuiltSlide[] {
  const built: BuiltSlide[] = [];

  for (const section of sections) {
    const headerChunks = chunkHeadersByWidth(section.headers);
    for (const chunk of headerChunks) {
      const projected = projectRows(
        section.rows.length ? section.rows : [[PLACEHOLDER_NO_DATA]],
        chunk.sourceIndexes,
      );
      const per = rowsPerSlideForColumnCount(chunk.headers.length);
      for (let offset = 0; offset < projected.length; offset += per) {
        const slice = projected.slice(offset, offset + per);
        const colNote = headerChunks.length > 1 ? ` · ${chunk.colRangeLabel}` : '';
        const sectionLabel = section.title
          ? `${section.title}${colNote}`
          : colNote
            ? chunk.colRangeLabel
            : undefined;
        built.push({
          paint: (slide, page, total) => {
            const cursor = new Cursor();
            if (section.metaRows?.length && offset === 0 && chunk === headerChunks[0]) {
              addMetaBlock(slide, cursor, section.metaRows);
            }
            addTableChunk(slide, cursor, chunk.headers, slice, sectionLabel);
            addChromeFooter(slide, `${footerLeft}${colNote}`, page, total);
          },
        });
      }
    }
  }
  return built;
}

async function writeDeck(built: BuiltSlide[], filename: string, footerLeft: string): Promise<void> {
  const PptxGenJS = await loadPptxGenJs();
  const pres = createDeck(PptxGenJS);
  const total = Math.max(built.length, 1);
  if (!built.length) {
    const slide = pres.addSlide();
    slide.addText(PLACEHOLDER_NO_DATA, {
      x: MARGIN_X,
      y: MARGIN_TOP,
      w: CONTENT_W,
      h: 1,
      fontFace: FONT_THAI,
      fontSize: 18,
      color: COLOR_INK,
      margin: 0,
    });
    addChromeFooter(slide, footerLeft, 1, 1);
  } else {
    built.forEach((item, index) => {
      const slide = pres.addSlide();
      slide.background = { color: COLOR_PAPER };
      item.paint(slide, index + 1, total, pres);
    });
  }
  const safeName = sanitizeDownloadFilename(filename, 'pptx');
  await pres.writeFile({ fileName: safeName });
}

export type ExportTableToPptxArgs = {
  title: string;
  headers?: string[];
  rows?: string[][];
  sections?: PptxSection[];
  metaRows?: PptxMetaRow[];
  filename: string;
  footerLeft?: string;
};

/** One dashboard chart (or capture section) as a PNG for a dedicated slide. */
export type PptxChartImage = {
  /** Must include `image/png;base64,` (or jpeg) prefix for pptxgenjs. */
  dataUrl: string;
  label: string;
  widthPx: number;
  heightPx: number;
  /**
   * When valid, PPTX uses a native editable PowerPoint chart.
   * PNG remains the fallback for complex / unsupported charts.
   */
  nativeChart?: PptxNativeChartSpec | null;
  /**
   * When true (chunked dense barH slides), never fall back to the full crowded PNG —
   * that screenshot would hide departments again.
   */
  preferNativeOnly?: boolean;
};

export type ExportDashboardSummaryToPptxArgs = ExportTableToPptxArgs & {
  subtitle?: string;
  kpis?: PptxKpi[];
  /** When present and non-empty, chart slides replace table slides (dashboard ≠ table). */
  chartImages?: PptxChartImage[];
};

function fitImageContain(
  widthPx: number,
  heightPx: number,
  maxW: number,
  maxH: number,
): { w: number; h: number } {
  if (!(widthPx > 0) || !(heightPx > 0)) {
    return { w: maxW, h: maxH };
  }
  const scale = Math.min(maxW / widthPx, maxH / heightPx);
  return { w: widthPx * scale, h: heightPx * scale };
}

function toPptxChartSeries(spec: PptxNativeChartSpec): Array<{
  name: string;
  labels: string[];
  values: number[];
}> {
  return spec.series.map((series) => ({
    name: series.name,
    labels: spec.labels,
    values: series.values,
  }));
}

function paintNativeChart(
  slide: PptxSlide,
  pres: PptxGenJS,
  spec: PptxNativeChartSpec,
  box: { x: number; y: number; w: number; h: number },
): void {
  const chartTypes = (pres as unknown as { ChartType?: Record<string, unknown> }).ChartType;
  const data = toPptxChartSeries(spec);
  const baseOpts = {
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    showTitle: false,
    showLegend: spec.series.length > 1,
    showValue: false,
    chartColors: [COLOR_ACCENT, '0EA5E9', '14B8A6', 'F59E0B', 'EF4444'],
    catAxisLabelColor: COLOR_INK,
    valAxisLabelColor: COLOR_MUTED,
    catAxisLabelFontSize: 10,
    valAxisLabelFontSize: 10,
    catAxisLabelFontFace: FONT_THAI,
    valAxisLabelFontFace: FONT_THAI,
    legendFontFace: FONT_THAI,
    catGridLine: { style: 'none' as const },
    valGridLine: { color: 'E2E8F0', size: 0.5 },
  };

  const addChart = (
    slide as unknown as {
      addChart?: (type: unknown, series: unknown, opts: unknown) => void;
    }
  ).addChart;

  if (typeof addChart !== 'function' || !chartTypes) {
    throw new Error('pptxgenjs addChart unavailable');
  }

  if (spec.kind === 'line') {
    addChart.call(slide, chartTypes.line ?? chartTypes.LINE, data, baseOpts);
    return;
  }
  if (spec.kind === 'pie') {
    addChart.call(slide, chartTypes.pie ?? chartTypes.PIE, data, {
      ...baseOpts,
      showLegend: true,
      showPercent: true,
    });
    return;
  }
  // bar / barH → column or horizontal bar
  addChart.call(slide, chartTypes.bar ?? chartTypes.BAR, data, {
    ...baseOpts,
    barDir: spec.kind === 'barH' ? 'bar' : 'col',
  });
}

/**
 * Expand dense horizontal native charts into multiple slides (still one chart each)
 * so every department/category label remains readable.
 */
export function expandChartImagesForSlides(images: readonly PptxChartImage[]): PptxChartImage[] {
  const expanded: PptxChartImage[] = [];
  for (const image of images) {
    const native =
      image.nativeChart && isValidPptxNativeChart(image.nativeChart) ? image.nativeChart : null;
    if (!native || native.kind !== 'barH') {
      expanded.push(image);
      continue;
    }
    const chunks = chunkPptxNativeChart(native);
    if (chunks.length <= 1) {
      expanded.push({ ...image, nativeChart: native });
      continue;
    }
    const baseLabel = image.label?.trim() || 'แผนภูมิ';
    chunks.forEach((chunk, chunkIndex) => {
      expanded.push({
        dataUrl: image.dataUrl,
        label: `${baseLabel} (${chunkIndex + 1}/${chunks.length})`,
        widthPx: image.widthPx,
        heightPx: image.heightPx,
        nativeChart: chunk,
        /** Chunk slides must not fall back to the full crowded screenshot. */
        preferNativeOnly: true,
      });
    });
  }
  return expanded;
}

function buildChartSlides(images: PptxChartImage[], footerLeft: string): BuiltSlide[] {
  return expandChartImagesForSlides(images).map((image, index) => ({
    paint: (slide, page, total, pres) => {
      const cursor = new Cursor();
      const title = image.label?.trim() || `แผนภูมิ ${index + 1}`;
      addTitle(slide, cursor, title);

      const maxW = CONTENT_W;
      const maxH = Math.max(1.5, cursor.remaining() - 0.05);
      const native =
        image.nativeChart && isValidPptxNativeChart(image.nativeChart) ? image.nativeChart : null;
      const allowImageFallback = !image.preferNativeOnly;

      if (native) {
        const top = cursor.take(maxH, 0);
        try {
          paintNativeChart(slide, pres, native, { x: MARGIN_X, y: top, w: maxW, h: maxH });
          addChromeFooter(slide, footerLeft, page, total);
          return;
        } catch {
          if (allowImageFallback) {
            slide.addImage({
              data: image.dataUrl,
              x: MARGIN_X,
              y: top,
              w: maxW,
              h: maxH,
            });
            addChromeFooter(slide, footerLeft, page, total);
            return;
          }
          // Chunk without screenshot fallback: keep every label as text so none are dropped.
          const lines = native.labels.map((label, i) => {
            const value = native.series[0]?.values[i];
            const valueText =
              typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '—';
            return `${label}: ${valueText}`;
          });
          slide.addText(lines.join('\n'), {
            x: MARGIN_X,
            y: top,
            w: maxW,
            h: maxH,
            fontSize: 11,
            fontFace: FONT_THAI,
            color: COLOR_INK,
            valign: 'top',
            margin: 0,
          });
          addChromeFooter(slide, footerLeft, page, total);
          return;
        }
      }

      const fitted = fitImageContain(image.widthPx, image.heightPx, maxW, maxH);
      const top = cursor.take(fitted.h, 0);
      const left = MARGIN_X + (maxW - fitted.w) / 2;

      slide.addImage({
        data: image.dataUrl,
        x: left,
        y: top,
        w: fitted.w,
        h: fitted.h,
      });
      addChromeFooter(slide, footerLeft, page, total);
    },
  }));
}

export async function exportTableToPptx(args: ExportTableToPptxArgs): Promise<void> {
  const footerLeft = args.footerLeft ?? args.title;
  const sections = normalizeSections(args);
  const built: BuiltSlide[] = [];

  built.push({
    paint: (slide, page, total) => {
      const cursor = new Cursor();
      addTitle(slide, cursor, args.title);
      if (args.metaRows?.length) addMetaBlock(slide, cursor, args.metaRows);
      addChromeFooter(slide, footerLeft, page, total);
    },
  });

  built.push(...buildTableSlides(sections, footerLeft));
  await writeDeck(built, args.filename, footerLeft);
}

export async function exportDashboardSummaryToPptx(
  args: ExportDashboardSummaryToPptxArgs,
): Promise<void> {
  const footerLeft = args.footerLeft ?? args.title;
  const chartImages = (args.chartImages ?? []).filter((img) => !!img?.dataUrl);
  const useCharts = chartImages.length > 0;
  const sections = useCharts ? [] : normalizeSections(args);
  const built: BuiltSlide[] = [];

  built.push({
    paint: (slide, page, total) => {
      const cursor = new Cursor(1.8);
      // Cover only paints title/subtitle (+ optional meta). KPIs are a separate slide.
      const stackH = 0.55 + (args.subtitle ? 0.4 : 0) + 0.3;
      cursor.y = Math.max(MARGIN_TOP, (CANVAS_H - stackH) / 2);
      addTitle(slide, cursor, args.title, args.subtitle);
      if (args.metaRows?.length) addMetaBlock(slide, cursor, args.metaRows.slice(0, 6));
      addChromeFooter(slide, footerLeft, page, total);
    },
  });

  if (args.kpis?.length) {
    built.push({
      paint: (slide, page, total) => {
        const cursor = new Cursor();
        addTitle(slide, cursor, 'ตัวชี้วัดสรุป');
        addKpiCards(slide, cursor, args.kpis ?? []);
        addChromeFooter(slide, footerLeft, page, total);
      },
    });
  }

  if (useCharts) {
    built.push(...buildChartSlides(chartImages, footerLeft));
  } else {
    built.push(...buildTableSlides(sections, footerLeft));
  }
  await writeDeck(built, args.filename, footerLeft);
}

export function pptxSlideBudgetDisclosure(projectedSlides: number): string | null {
  if (projectedSlides <= PPTX_SOFT_SLIDE_BUDGET) return null;
  return `ไฟล์มีประมาณ ${projectedSlides} สไลด์ ซึ่งมากกว่าปกติ อาจเปิดช้าบนเครื่องบางเครื่อง`;
}
